import { isString, isUndefined } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type ClipAssets, ClipStatus, ClipType } from '@air/api/types';
import { buildClientImgixUrl, isImgixUrl, resolveByosImgixSrc } from '@air/component-imgix-image';

import { getCloudFrontUrlFromImgixUrl, getImagePreviewFallbackUrl } from '~/constants/cloudfront';
import {
  centralizedClipAssetsSelector,
  centralizedClipExtSelector,
  centralizedClipStatusSelector,
  centralizedClipTypeSelector,
} from '~/store/centralizedClip/selectors';
import {
  getTransparentPreviewSource,
  isTransparentPreviewExtension,
  TRANSPARENT_PREVIEW_IMGIX_PARAMS,
  type TransparentPreviewSource,
} from '~/utils/getTransparentPreviewSource';
import { extCanBeDisplayedNatively, extCanBeExported } from '~/utils/Imgix';

const getMostCompatibleImageUrl = (url?: string): string | undefined => {
  if (!isString(url)) {
    return url;
  }

  const ext = url.split('.').pop()?.toLowerCase();

  // If the image is an Imgix URL and the extension is supported, return the optimized Imgix URL
  // If the image can be rendered natively in-browser, show as-is with no format conversion
  // E.g. if the file type is PSD, we're able to render a representation of it
  // Reference: https://docs.imgix.com/en-US/references/supported-file-formats
  if (ext && extCanBeExported(ext) && !extCanBeDisplayedNatively(ext) && isImgixUrl(url)) {
    return buildClientImgixUrl(
      url,
      isTransparentPreviewExtension(ext) ? TRANSPARENT_PREVIEW_IMGIX_PARAMS : { auto: 'format' },
    );
  }

  return url;
};

const getFallbackUrl = (url: string, keepSourceWhenUnmapped: boolean): string => {
  try {
    const updatedUrl = new URL(url);
    updatedUrl.search = '';
    const stripped = updatedUrl.toString();

    if (!keepSourceWhenUnmapped) {
      return getCloudFrontUrlFromImgixUrl(stripped);
    }

    return getImagePreviewFallbackUrl(stripped);
  } catch {
    return url;
  }
};

export type SuitableImageUrlResult = {
  fallbackUrl: string | undefined;
  preserveTransparency: boolean;
  /** Processed JPEG to request through Imgix when the original rendition fails and has no plain-image URL. */
  processedPreviewSrc: string | undefined;
  src: string | undefined;
};

type ImagePreviewSelection = {
  fallbackSource: string | undefined;
  preserveTransparency: boolean;
  processedPreviewSrc: string | undefined;
  src: string | undefined;
};

/** `ClipAssets.image` is typed as a string, but clips omit it until a preview exists. */
type ClipPreviewAssets = Partial<Pick<ClipAssets, 'image' | 'original' | 'video'>>;

type SelectImagePreviewParams = {
  clipAssets: ClipPreviewAssets;
  clipExt: string;
  clipStatus: ClipStatus;
  clipType: ClipType;
  showOriginal: boolean;
};

const selectOriginalPreview = (
  transparentPreview: TransparentPreviewSource,
  resolvedOriginal: string | undefined,
  compatibleOriginalUrl: string,
): ImagePreviewSelection => ({
  fallbackSource: resolvedOriginal,
  preserveTransparency: transparentPreview.preserveTransparency,
  processedPreviewSrc: undefined,
  src: compatibleOriginalUrl,
});

const selectProcessedPreview = (
  clipAssets: ClipPreviewAssets,
  transparentPreview: TransparentPreviewSource,
): ImagePreviewSelection => {
  if (transparentPreview.preserveTransparency && transparentPreview.src) {
    const src = resolveByosImgixSrc(transparentPreview.src);
    const fallbackSource = transparentPreview.fallbackSrc ? resolveByosImgixSrc(transparentPreview.fallbackSrc) : src;

    return {
      fallbackSource,
      preserveTransparency: true,
      processedPreviewSrc: fallbackSource !== src ? fallbackSource : undefined,
      src,
    };
  }

  const imageOrOriginal = clipAssets.image ?? clipAssets.original;
  const src = imageOrOriginal ? resolveByosImgixSrc(imageOrOriginal) : imageOrOriginal;

  return { fallbackSource: src, preserveTransparency: false, processedPreviewSrc: undefined, src };
};

const selectImagePreview = ({
  clipAssets,
  clipExt,
  clipStatus,
  clipType,
  showOriginal,
}: SelectImagePreviewParams): ImagePreviewSelection => {
  const transparentPreview = getTransparentPreviewSource(clipExt, clipAssets);
  const resolvedOriginal = clipAssets.original ? resolveByosImgixSrc(clipAssets.original) : clipAssets.original;
  const compatibleOriginalUrl = getMostCompatibleImageUrl(resolvedOriginal);

  if (clipType === ClipType.animated && clipAssets.video) {
    const src = resolveByosImgixSrc(clipAssets.video);

    return { fallbackSource: src, preserveTransparency: false, processedPreviewSrc: undefined, src };
  }

  if (clipStatus === ClipStatus.failed && !isUndefined(clipAssets.original)) {
    const src = resolveByosImgixSrc(clipAssets.original);

    return {
      fallbackSource: src,
      preserveTransparency: transparentPreview.preserveTransparency,
      processedPreviewSrc: undefined,
      src,
    };
  }

  if (showOriginal && !isUndefined(compatibleOriginalUrl)) {
    return selectOriginalPreview(transparentPreview, resolvedOriginal, compatibleOriginalUrl);
  }

  return selectProcessedPreview(clipAssets, transparentPreview);
};

export const useSuitableImageUrl = (showOriginal: boolean): SuitableImageUrlResult => {
  const clipAssets = useSelector(centralizedClipAssetsSelector);
  const clipExt = useSelector(centralizedClipExtSelector);
  const clipStatus = useSelector(centralizedClipStatusSelector);
  const clipType = useSelector(centralizedClipTypeSelector);

  const preview = useMemo(
    () => selectImagePreview({ clipAssets, clipExt, clipStatus, clipType, showOriginal }),
    [clipAssets, clipExt, clipStatus, clipType, showOriginal],
  );

  const fallbackUrl = useMemo(() => {
    const source = preview.fallbackSource;
    let resolved = source;

    if (source) {
      resolved = getFallbackUrl(source, preview.preserveTransparency);
    }

    return resolved;
  }, [preview.fallbackSource, preview.preserveTransparency]);

  return {
    fallbackUrl,
    preserveTransparency: preview.preserveTransparency,
    processedPreviewSrc: preview.processedPreviewSrc,
    src: preview.src,
  };
};
